/**
 * **O Ciclone de Lâminas: uma meia-lua que corta, desenhada de um facto.**
 *
 * ## Quem manda desenhar, e quem nunca manda
 *
 * Este módulo desenha um `proc` que a AUTORIDADE confirmou. Ele não sabe o que é
 * um perfil, um socket, uma cópia, um nível ou uma fórmula, e não tem por onde
 * saber: a sua única entrada é `{ id, x, y, diameter }` - onde bater, e que
 * largura o alvo tem DESENHADA, que a cena mede na mesma fonte por onde já o
 * desenha. Nem sequer a vida atual do alvo entra: um casco não encolhe ao ser
 * atingido, e o corte que o atravessa também não. Não infere nada pelo dano, pela
 * textura do tiro nem pelo que a nave tem equipado.
 *
 * O corte é um golpe adicional **no inimigo**. Não é uma decoração do projétil,
 * do canhão nem da nave que disparou, e nada aqui toca em nenhum dos três.
 *
 * ## Porque a forma é PREENCHIDA, e nunca um traço
 *
 * A versão anterior foi reprovada, e a causa estava na ferramenta. Ela desenhava
 * a lâmina com `lineStyle` + `arc` + `strokePath` - um TRAÇO. Um traço tem
 * espessura constante e termina em tampas redondas, que é exatamente o que se
 * viu: "as pontas são suaves e arredondadas", "não parece uma lâmina". Somando
 * três traços e três fragmentos, o que restava era "três pinceladas luminosas" e
 * "uma partícula azul que demora a desaparecer".
 *
 * Uma lâmina não é um traço: é um CORPO. Esta versão constrói um polígono
 * fechado - curva exterior, ponta, curva interior de volta, ponta - em que a
 * espessura é máxima no meio e cai continuamente até **zero exato** nas duas
 * extremidades. As duas curvas encontram-se no mesmo ponto em cada ponta, por
 * construção e não por aproximação, e é isso que produz duas pontas agudas.
 *
 * Não há `roundCap`, não há disco nas extremidades, e não há desfoque - qualquer
 * um dos três reconstruiria a ponta redonda que a crítica recusou.
 *
 * ## Duas camadas, e só duas
 *
 * 1. **corpo** - a meia-lua cobalto, sólida e afiada;
 * 2. **fio** - uma lâmina mais fina, quase branca, sobre PARTE da borda cortante.
 *
 * Não há fragmentos, partículas, fumo, faíscas, pincelada secundária nem uma
 * terceira lâmina. Nada continua depois do corte.
 *
 * ## O movimento é seco
 *
 * Nasce grande, atinge o pico nos primeiros milissegundos, desloca-se um pouco
 * ao longo do golpe e desaparece. A vida inteira é curta de propósito: uma
 * lâmina que se expande devagar e fica a flutuar é uma energia, e a leitura que
 * se quer é "uma lâmina acabou de cortar este alvo".
 */

/** Um corte confirmado, já em píxeis da cena. */
export interface CycloneCut {
  /**
   * O id do evento, e a semente da variação.
   *
   * Derivar a rotação dele - em vez de a sortear - é o que faz seis clientes
   * verem o MESMO corte. Um `Math.random()` aqui daria a cada browser uma
   * animação diferente para um facto que é um só.
   */
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /**
   * **O diâmetro DESENHADO do alvo atingido**, em píxeis da cena.
   *
   * A cena mede-o na fonte que ela própria já usa para dimensionar o casco, o
   * mob ou o boss, e entrega o número. Este módulo não sabe o que é uma nave,
   * um mob, um boss, uma vida ou uma definição - recebe um comprimento.
   *
   * Sem ele o corte tinha um tamanho fixo, e uma lâmina de 50 px sobre um boss
   * lia-se como um risco, enquanto sobre um mob pequeno tapava o alvo inteiro.
   */
  readonly diameter: number;
}

/**
 * A vida visual inteira, em ms.
 *
 * Curta por decisão de arte, e é metade da correção: a 200 ms o efeito lia-se
 * como "uma partícula azul que demora a desaparecer". Um corte acontece e acaba.
 */
export const CYCLONE_CUT_DURATION_MS = 110;

/** Quando a lâmina atinge o seu tamanho máximo. */
export const CYCLONE_CUT_PEAK_MS = 18;

/**
 * O lado da textura, em píxeis da arena.
 *
 * A meia-lua ocupa ~78 % desta largura, e a textura é desenhada no tamanho da
 * região visual de um casco atingido. Não cresceu para compensar a forma
 * anterior: a forma é que passou a ler-se.
 */
export const CYCLONE_CUT_DIAMETER = 64;

/**
 * **Quantos cortes podem estar vivos ao mesmo tempo.**
 *
 * Cento e quarenta e quatro, DERIVADO da maior rajada que as regras permitem -
 * nunca escolhido por parecer suficiente.
 *
 * Este número era doze, e doze era um palpite. Um evento que a autoridade
 * confirmou não pode desaparecer por um limite de desenho, portanto o teto tem
 * de ficar acima de tudo o que o jogo pode legitimamente produzir dentro de uma
 * vida de corte - {@link CYCLONE_CUT_DURATION_MS}.
 *
 * ## As três rajadas legítimas, e de onde saem
 *
 * | caso | conta | cortes em 110 ms |
 * | --- | --- | --- |
 * | caça, hoje | 21 projéteis/salva × 31,72 ataques/s × 0,110 s | 74 |
 * | duelo 3×3 | 6 naves × 15 tiros em voo | 90 |
 * | caça, canhão Prismático reservado | 28 projéteis/salva × 31,72 × 0,110 | 98 |
 *
 * 21 é o nível 7 do canhão Anomalia, o maior disponível; 31,72 é a cadência
 * base somada ao Speed Fast nível 10, que é o extremo que o próprio projeto
 * escreve; 15 em voo é `lifetime / interval` do duelo.
 *
 * 144 sobre 98 é uma margem de 1,47, a mesma disciplina que
 * `PVP_MAX_PROJECTILES` mantém sobre o seu próprio pior caso. A derivação está
 * afirmada em `cyclone-cut.test.ts`, contra as constantes reais - este módulo
 * não pode importar o balanceamento do duelo nem o da caça sem passar a saber o
 * que é uma nave.
 *
 * ## O que isto NÃO é
 *
 * Não é uma reserva. O pool cresce por procura: uma caçada comum tem dois ou
 * três cortes vivos e aloca quatro ou seis sprites. O teto só limita o
 * crescimento, portanto subi-lo não custa memória a quem nunca lá chega.
 *
 * E não é o máximo verdadeiro. A cadência da caça é **descapada por desenho** -
 * os pontos livres são ilimitados e somam 0,02 ataques por ponto - portanto não
 * existe um limite finito que cubra todo o jogo futuro. Isto é um limite sobre
 * um DEFEITO, como o teto de projéteis do duelo: chegar-lhe significa que uma
 * cadência saiu do que este cálculo previu, e a resposta certa é refazer a
 * conta acima, não descartar um corte em silêncio.
 */
export const CYCLONE_CUT_POOL = 144;

const BODY_TEXTURE = 'cyclone-cut-body';
const EDGE_TEXTURE = 'cyclone-cut-edge';

/**
 * A assinatura, em azul e mais nada.
 *
 * Cobalto no corpo, quase branco no fio. Nunca uma cor de fogo, de veneno, de
 * cura ou de escudo - a joia é azul-escura, e a cor é metade do que diz ao
 * jogador qual dos efeitos aconteceu.
 */
const NEAR_WHITE = 0xf2f8ff;
const COBALT = 0x2f6bff;

/* -------------------------------------------------------------------------- */
/*  A geometria                                                               */
/* -------------------------------------------------------------------------- */

/** Uma meia-lua, como um polígono fechado. */
export interface CrescentShape {
  /** A curva EXTERIOR, da ponta inicial à ponta final. */
  readonly outer: readonly { readonly x: number; readonly y: number }[];
  /** A curva INTERIOR, na mesma ordem. */
  readonly inner: readonly { readonly x: number; readonly y: number }[];
  /** A espessura em cada amostra, na mesma ordem. Zero nas duas pontas. */
  readonly thickness: readonly number[];
}

/**
 * **A meia-lua, calculada e não desenhada.**
 *
 * Uma curva central em arco, e uma espessura que é máxima no meio e cai a ZERO
 * nas duas extremidades. A curva exterior é a central mais metade da espessura;
 * a interior é a central menos metade. Nas pontas a espessura é exatamente zero,
 * portanto as duas curvas **coincidem no mesmo ponto** - é assim que a ponta
 * fica aguda, por construção.
 *
 * A separação entre calcular e desenhar é deliberada: uma prova pode medir a
 * espessura ponta a ponta sem abrir um canvas, que é o que `cyclone-cut.test.ts`
 * faz para recusar extremidades circulares e espessura constante.
 *
 * @param radius raio da curva central. Maior = meia-lua mais aberta.
 * @param sweep meio-ângulo do arco, em radianos. Decide a largura.
 * @param thickest espessura no meio, em píxeis.
 * @param samples quantos pontos por curva.
 */
export function crescent(
  radius: number,
  sweep: number,
  thickest: number,
  samples = 48,
): CrescentShape {
  const half = CYCLONE_CUT_DIAMETER / 2;
  /*
   * O centro do arco fica ABAIXO da textura, para o topo do arco passar
   * exatamente pelo meio dela. É isso que faz a lâmina ATRAVESSAR o ponto
   * atingido em vez de andar à volta dele.
   */
  const cx = half;
  const cy = half + radius;
  const outer: { x: number; y: number }[] = [];
  const inner: { x: number; y: number }[] = [];
  const thickness: number[] = [];

  for (let i = 0; i <= samples; i += 1) {
    const u = (i / samples) * 2 - 1; // -1 .. 1
    const angle = -Math.PI / 2 + u * sweep;
    /*
     * O afinamento. `(1 - u²)` já vai a zero nas pontas; o expoente 0,62 mantém
     * o meio cheio e torna a queda perto da ponta mais longa e mais fina, que é
     * a diferença entre uma lasca e uma agulha.
     */
    const taper = (1 - u * u) ** 0.62;
    const t = thickest * taper;
    const nx = Math.cos(angle);
    const ny = Math.sin(angle);
    outer.push({ x: cx + nx * (radius + t / 2), y: cy + ny * (radius + t / 2) });
    inner.push({ x: cx + nx * (radius - t / 2), y: cy + ny * (radius - t / 2) });
    thickness.push(t);
  }
  return { outer, inner, thickness };
}

/**
 * O contorno fechado, na ordem em que um preenchimento o quer.
 *
 * Exterior da ponta inicial à final, interior de volta. O fecho é implícito
 * porque o primeiro e o último ponto coincidem - a espessura é zero nos dois.
 */
export function crescentOutline(shape: CrescentShape): { x: number; y: number }[] {
  return [...shape.outer, ...[...shape.inner].reverse()];
}

/** A largura da meia-lua, de ponta a ponta, em píxeis da textura. */
export function crescentSpan(shape: CrescentShape): number {
  const xs = crescentOutline(shape).map((point) => point.x);
  return Math.max(...xs) - Math.min(...xs);
}

/**
 * A largura desenhada do CORPO, à escala 1. **Derivada**, nunca escrita.
 *
 * É o denominador de toda a proporção abaixo. Escrevê-la à mão seria um número
 * que deixa de estar certo no dia em que o varrimento mudar.
 */
export const CYCLONE_CUT_SPAN = crescentSpan(crescent(38, 0.72, 7));

/**
 * A largura do ALVO sobre o qual a forma foi aprovada, em unidades da arena.
 *
 * Cinquenta e dois. É um comprimento medido na captura aprovada e escrito aqui
 * como um número: este módulo não pode importar a constante do duelo sem passar
 * a saber o que é uma nave, e `cyclone-cut.test.ts` afirma a igualdade no sítio
 * onde conhecer os dois lados é legítimo.
 */
export const CYCLONE_CUT_REFERENCE_TARGET = 52;

/**
 * **O contrato visual: quanto da largura do alvo a lâmina ocupa.**
 *
 * DERIVADO da medição, e nunca escrito à mão. Na versão aprovada o corpo tinha
 * `CYCLONE_CUT_SPAN` de largura desenhada sobre um alvo de
 * `CYCLONE_CUT_REFERENCE_TARGET`, e a razão entre os dois é o contrato:
 *
 *     diâmetro do corte = diâmetro desenhado do alvo × esta razão
 *
 * Um alvo a metade recebe metade; um boss recebe um corte de boss. E o alvo de
 * referência recebe **exatamente** o que foi aprovado - por construção, porque
 * o numerador e o denominador saem da mesma medição. É isso que torna esta
 * mudança uma generalização em vez de uma arte nova.
 */
export const CYCLONE_CUT_TARGET_RATIO = CYCLONE_CUT_SPAN / CYCLONE_CUT_REFERENCE_TARGET;

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface CycloneCutScene {
  readonly textures: { exists(key: string): boolean };
  readonly tweens: {
    add(config: Record<string, unknown>): { remove(): void };
  };
  make: {
    graphics(config?: unknown, addToScene?: boolean): CycloneCutGraphics;
  };
  add: {
    image(x: number, y: number, key: string): CycloneCutSprite;
  };
}

export interface CycloneCutGraphics {
  fillStyle(color: number, alpha?: number): CycloneCutGraphics;
  fillPoints(
    points: readonly { x: number; y: number }[],
    closeShape?: boolean,
    closePath?: boolean,
  ): CycloneCutGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

export interface CycloneCutSprite {
  x: number;
  y: number;
  rotation: number;
  alpha: number;
  visible: boolean;
  setDepth(value: number): CycloneCutSprite;
  setScale(x: number, y?: number): CycloneCutSprite;
  setVisible(value: boolean): CycloneCutSprite;
  destroy(): void;
}

/**
 * As duas texturas, criadas UMA vez por cena e reutilizadas para sempre.
 *
 * `textures.exists` antes de desenhar, porque uma cena reiniciada partilha o
 * gestor de texturas do jogo: regenerar seria pagar o mesmo desenho a cada
 * abertura de arena.
 */
function ensureTextures(scene: CycloneCutScene): void {
  if (!scene.textures.exists(BODY_TEXTURE)) {
    /*
     * O CORPO: uma meia-lua cobalto, sólida, com o interior vazio.
     *
     * Vazio por construção e não por um recorte: o polígono é a faixa entre as
     * duas curvas, portanto o que fica dentro da curvatura nunca chega a ser
     * pintado.
     */
    const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
    graphics.fillStyle(COBALT, 1);
    graphics.fillPoints(crescentOutline(crescent(38, 0.72, 7)), true);
    graphics.generateTexture(BODY_TEXTURE, CYCLONE_CUT_DIAMETER, CYCLONE_CUT_DIAMETER);
    graphics.destroy();
  }

  if (!scene.textures.exists(EDGE_TEXTURE)) {
    /*
     * O FIO: uma meia-lua muito mais fina, quase branca, sobre a MESMA curva.
     *
     * Um raio ligeiramente maior põe-na do lado de fora - a borda cortante - e
     * um varrimento mais curto faz com que ela acompanhe só PARTE dela, em vez
     * de contornar a lâmina inteira e a transformar noutro traço.
     */
    const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
    graphics.fillStyle(NEAR_WHITE, 1);
    graphics.fillPoints(crescentOutline(crescent(40.4, 0.6, 2.2)), true);
    graphics.generateTexture(EDGE_TEXTURE, CYCLONE_CUT_DIAMETER, CYCLONE_CUT_DIAMETER);
    graphics.destroy();
  }
}

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

interface Live {
  readonly body: CycloneCutSprite;
  readonly edge: CycloneCutSprite;
  readonly tweens: { remove(): void }[];
  eventId: number;
  active: boolean;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * Nada aqui é global e nada sobrevive a `destroy()`: os sprites são deste
 * sistema, os tweens são registados e removidos, e as texturas ficam no gestor
 * da cena, que é quem as descarta quando a cena morre.
 */
export class CycloneCutSystem {
  private readonly pool: Live[] = [];
  private next = 0;
  private disposed = false;

  constructor(
    private readonly scene: CycloneCutScene,
    private readonly depth = 900,
  ) {
    ensureTextures(scene);
  }

  /**
   * Desenha um corte confirmado.
   *
   * Idempotência é responsabilidade de quem entrega: a fila da ligação e a
   * drenagem da simulação já garantem que um evento chega uma vez. Este método
   * desenha o que lhe derem - um sistema que também deduplicasse seria uma
   * segunda memória a envelhecer ao lado da primeira.
   */
  spawn(cut: CycloneCut): void {
    if (this.disposed) {
      return;
    }
    const live = this.take();
    live.eventId = cut.id;
    live.active = true;

    /*
     * A VARIAÇÃO vem do id do evento, e nunca de um sorteio local.
     *
     * Seis clientes desenham o mesmo facto: se cada um rodasse o corte para o
     * seu lado, dois jogadores a ver o mesmo golpe veriam coisas diferentes.
     */
    const spin = ((cut.id * 2654435761) % 360) * (Math.PI / 180);

    /*
     * **A escala, e é a única coisa que este gate acrescentou.**
     *
     * O alvo diz a sua largura desenhada; o contrato diz que fração dela a
     * lâmina ocupa; e `CYCLONE_CUT_SPAN` diz quanto o corpo mede à escala 1. O
     * quociente é o fator, e ele multiplica X e Y pelo MESMO valor - uma
     * meia-lua esticada num eixo deixa de ser a forma aprovada.
     *
     * Sem teto e sem chão. Um limite arbitrário aqui reintroduziria o problema
     * que isto resolve: quem decide o tamanho é o alvo, e ele já tem os limites
     * que tem.
     */
    const fit = (cut.diameter * CYCLONE_CUT_TARGET_RATIO) / CYCLONE_CUT_SPAN;

    /*
     * A direção do golpe, para o pequeno deslocamento do corte.
     *
     * A distância acompanha a escala pela mesma razão que o corpo: cinco píxeis
     * sobre um boss não se veem, e sobre um mob pequeno são um salto.
     */
    const slide = 5 * fit;
    const slideX = Math.cos(spin + Math.PI / 2) * slide;
    const slideY = Math.sin(spin + Math.PI / 2) * slide;

    for (const sprite of [live.body, live.edge]) {
      sprite.x = cut.x;
      sprite.y = cut.y;
      sprite.rotation = spin;
      sprite.alpha = 1;
      /* Nasce quase no tamanho final: uma lâmina não cresce, ela passa. */
      sprite.setScale(0.92 * fit);
      sprite.setVisible(true);
      /*
       * O PICO, nos primeiros milissegundos. Um `easeOut` põe quase todo o
       * crescimento no princípio, que é onde a leitura acontece.
       */
      this.push(live, {
        targets: sprite,
        scaleX: 1.04 * fit,
        scaleY: 1.04 * fit,
        duration: CYCLONE_CUT_PEAK_MS,
        ease: 'Cubic.easeOut',
      });
    }

    /*
     * O corte a passar: um deslocamento pequeno e uma rotação curta ao longo da
     * vida. Não é uma órbita e não é uma expansão - é o gesto a terminar.
     */
    this.push(live, {
      targets: [live.body, live.edge],
      x: cut.x + slideX,
      y: cut.y + slideY,
      rotation: spin + 0.16,
      duration: CYCLONE_CUT_DURATION_MS,
      ease: 'Quad.easeOut',
    });

    /*
     * O desaparecimento, e o regresso ao pool NO MESMO instante.
     *
     * `Quad.easeIn` mantém a lâmina cheia quase até ao fim e depois apaga-a
     * depressa, portanto o alpha só chega a zero no último quadro - e o
     * `onComplete` larga o slot aí. Não há janela de sprites ativos invisíveis
     * para lá de um quadro.
     */
    this.push(live, {
      targets: [live.body, live.edge],
      alpha: 0,
      duration: CYCLONE_CUT_DURATION_MS,
      ease: 'Quad.easeIn',
      onComplete: () => this.release(live),
    });
  }

  /** Quantos cortes estão vivos agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.pool.filter((entry) => entry.active).length;
  }

  /** Quantos objetos o sistema alocou ao todo. Nunca deve crescer sem uso. */
  pooledCount(): number {
    return this.pool.length;
  }

  /**
   * Apaga tudo o que está no ecrã, sem largar os objetos.
   *
   * Para uma CORREÇÃO ou uma substituição de mundo: o que estava a ser desenhado
   * pertencia a um estado que já não existe.
   */
  clear(): void {
    for (const live of this.pool) {
      this.release(live);
    }
  }

  /**
   * Larga tudo. Depois disto o sistema não desenha mais nada.
   *
   * Os tweens são removidos ANTES dos sprites: um tween cujo alvo foi destruído
   * continua a correr e a escrever num objeto morto.
   */
  destroy(): void {
    this.disposed = true;
    for (const live of this.pool) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.body.destroy();
      live.edge.destroy();
    }
    this.pool.length = 0;
  }

  private push(live: Live, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private release(live: Live): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    live.body.setVisible(false);
    live.edge.setVisible(false);
  }

  /**
   * Um slot do pool: reaproveitado se houver um livre, criado se não houver.
   *
   * ## O último recurso, e porque é o mais antigo
   *
   * Ao teto - e só lá - o mais antigo é reciclado. Isso INTERROMPE um corte, o
   * que nenhuma rajada legítima deve provocar: {@link CYCLONE_CUT_POOL} está
   * derivado acima de todas elas, e `cyclone-cut.test.ts` prova que a maior
   * delas produz N sinais para N eventos sem reciclar nada.
   *
   * Quando acontecer mesmo assim, a escolha é entre calar o corte NOVO e
   * abreviar o mais VELHO. Abrevia-se o velho: ele já viveu a maior parte dos
   * seus 110 ms e o novo é o golpe que o jogador está a ver acontecer. Calar o
   * novo esconderia o evento mais recente, que é o oposto do que este sistema
   * existe para fazer.
   */
  private take(): Live {
    const free = this.pool.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    if (this.pool.length >= CYCLONE_CUT_POOL) {
      const oldest = this.pool[this.next % this.pool.length] as Live;
      this.next += 1;
      this.release(oldest);
      return oldest;
    }
    const created = this.build();
    this.pool.push(created);
    return created;
  }

  private build(): Live {
    const body = this.scene.add.image(0, 0, BODY_TEXTURE);
    body.setDepth(this.depth - 1).setVisible(false);
    const edge = this.scene.add.image(0, 0, EDGE_TEXTURE);
    edge.setDepth(this.depth).setVisible(false);
    return { body, edge, tweens: [], eventId: -1, active: false };
  }
}
